import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiClient } from '@/lib/api'
import { explainError } from '@/lib/explain-error'
import { formatCurrency, formatDate } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { AccountSearchPicker } from '@/components/accounts/AccountSearchPicker'
import type { BillingCustomer, BillingCustomerStatus } from '@/types'

const FILTERS: { key: BillingCustomerStatus; label: string }[] = [
  { key: 'unmatched', label: 'Not linked' },
  { key: 'auto', label: 'Matched by name' },
  { key: 'manual', label: 'Linked by hand' },
  { key: 'none', label: 'Not a GRM account' },
  { key: 'all', label: 'All' },
]

const METHOD_LABEL: Record<string, string> = {
  auto: 'Matched by name',
  manual: 'Linked by hand',
  none: 'Not a GRM account',
}

/**
 * Sales Trackers > Customer accounts: which GRM account each QuickBooks customer is, so
 * billed spend appears on the right account (Top Accounts > Billed spend). Admin only.
 */
export default function CustomerAccounts() {
  useDocumentTitle('Customer accounts · Sales Trackers')
  const { toast } = useToast()
  const qc = useQueryClient()
  const [status, setStatus] = useState<BillingCustomerStatus>('unmatched')
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search, 300)

  const listQuery = useQuery({
    queryKey: ['billings', 'customer-accounts', status, debounced],
    queryFn: () => apiClient.getBillingCustomers({ status, search: debounced || undefined }),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['billings', 'customer-accounts'] })
    qc.invalidateQueries({ queryKey: ['billings', 'account-spend'] })
  }

  const save = useMutation({
    mutationFn: (body: { customer_name: string; account_id?: string; not_an_account?: boolean }) =>
      apiClient.setBillingCustomerAccount(body),
    onSuccess: (_r, body) => {
      invalidate()
      toast({
        title: body.not_an_account ? 'Marked as not a GRM account' : 'Customer linked',
        description: body.customer_name,
      })
    },
    onError: (e) =>
      toast({
        title: "That link wasn't saved",
        description: explainError(e, 'Try again in a minute.'),
        variant: 'destructive',
      }),
  })

  const autoMatch = useMutation({
    mutationFn: () => apiClient.autoMatchBillingCustomers(),
    onSuccess: (r) => {
      invalidate()
      toast({
        title: r.linked
          ? `${r.linked} customer${r.linked === 1 ? '' : 's'} matched by name`
          : 'No new name matches',
      })
    },
    onError: (e) =>
      toast({
        title: "Customers couldn't be matched",
        description: explainError(e, 'Try again in a minute.'),
        variant: 'destructive',
      }),
  })

  const summary = Object.fromEntries(
    (listQuery.data?.summary ?? []).map((s) => [s.status, s])
  ) as Record<string, { customers: number; billed: number } | undefined>
  const rows: BillingCustomer[] = listQuery.data?.rows ?? []

  return (
    <main className="space-y-6" aria-labelledby="customer-accounts-title">
      <div>
        <h1 id="customer-accounts-title" className="text-3xl font-bold tracking-tight">
          Customer accounts
        </h1>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          Which GRM account each QuickBooks customer is, so billed spend shows on the right account
          in{' '}
          <Link to="/reports/top-accounts" className="underline">
            Top Accounts
          </Link>
          . Customers are matched by name automatically after every upload; link the rest here, or
          mark one-off buyers as not a GRM account.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {(['unmatched', 'auto', 'manual', 'none'] as const).map((k) => (
          <div key={k} className="rounded-md border p-3">
            <span className="block text-xs font-medium text-muted-foreground">
              {k === 'unmatched' ? 'Not linked' : METHOD_LABEL[k]}
            </span>
            <span className="block text-lg font-bold tabular-nums">
              {summary[k]?.customers ?? 0}
            </span>
            <span className="block text-xs tabular-nums">
              {formatCurrency(summary[k]?.billed ?? 0, 'CAD')} billed, last 2 years
            </span>
          </div>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>QuickBooks customers</CardTitle>
          <CardDescription>
            Largest billed first (this fiscal year and last). Choosing an account saves straight
            away.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-1" role="group" aria-label="Show customers">
              {FILTERS.map((f) => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={status === f.key ? 'default' : 'outline'}
                  aria-pressed={status === f.key}
                  onClick={() => setStatus(f.key)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            <div className="space-y-1">
              <Label htmlFor="customer-search">Search</Label>
              <Input
                id="customer-search"
                className="w-64"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Customer or account name"
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => autoMatch.mutate()}
              disabled={autoMatch.isPending}
            >
              {autoMatch.isPending ? 'Matching…' : 'Match by name now'}
            </Button>
          </div>

          {listQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : listQuery.isError ? (
            <p className="text-sm text-destructive">
              {explainError(listQuery.error, "The customers couldn't be loaded.")}
            </p>
          ) : !rows.length ? (
            <p className="text-sm text-muted-foreground">
              {status === 'unmatched'
                ? 'Every QuickBooks customer is linked or marked.'
                : 'No customers here.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                aria-label="QuickBooks customers and their accounts"
              >
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th scope="col" className="p-2 text-left font-medium">
                      QuickBooks customer
                    </th>
                    <th scope="col" className="p-2 text-right font-medium">
                      Billed
                    </th>
                    <th scope="col" className="p-2 text-left font-medium">
                      Last billed
                    </th>
                    <th scope="col" className="p-2 text-left font-medium">
                      GRM account
                    </th>
                    <th scope="col" className="p-2 text-left font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.customer_key} className="border-b last:border-0 align-top">
                      <td className="p-2">
                        <span className="font-medium">{c.customer_name}</span>
                        {c.method && (
                          <span className="block text-xs text-muted-foreground">
                            {METHOD_LABEL[c.method]}
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-right tabular-nums">
                        {formatCurrency(c.billed, 'CAD')}
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        {c.last_billed ? formatDate(c.last_billed) : '–'}
                      </td>
                      <td className="p-2 min-w-[280px]">
                        <AccountSearchPicker
                          id={`acct-${c.customer_key}`}
                          value={c.account_id ?? ''}
                          selectedLabel={c.account_name ?? undefined}
                          initialSearch={c.account_id ? '' : c.customer_name.slice(0, 30)}
                          ariaLabel={`GRM account for ${c.customer_name}`}
                          onChange={(id) =>
                            id &&
                            id !== c.account_id &&
                            save.mutate({ customer_name: c.customer_name, account_id: id })
                          }
                        />
                        {c.account_id && c.account_owner && (
                          <span className="block text-xs text-muted-foreground mt-1">
                            Owner: {c.account_owner}
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        {c.method !== 'none' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              save.mutate({ customer_name: c.customer_name, not_an_account: true })
                            }
                            aria-label={`Mark ${c.customer_name} as not a GRM account`}
                          >
                            Not a GRM account
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
